// --- START: Translated materials data hook ---
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { MaterialSpec, MaterialId } from './materials';

type TranslationFunction = ReturnType<typeof useTranslation>['t'];

/**
 * Hook that returns material specs with translations applied
 */
export function useMaterials(): MaterialSpec[] {
  const { t } = useTranslation('marketing');
  
  return useMemo(() => {
    const materialIds: MaterialId[] = ['PLA', 'PETG', 'ASA', 'TPU'];
    
    return materialIds.map((id, index) => {
      const base = `materialGuide.materials.${id}`;
      
      // Get array translations
      const pros = getArrayTranslation(t, `${base}.pros`);
      const cons = getArrayTranslation(t, `${base}.cons`);
      const bestFor = getArrayTranslation(t, `${base}.bestFor`);
      
      return {
        id,
        name: t(`${base}.name`),
        summary: t(`${base}.summary`),
        description: t(`${base}.description`),
        pros,
        cons,
        bestFor,
        traits: {
          heatResistance: t(`${base}.traits.heatResistance`),
          strength: t(`${base}.traits.strength`),
          flexibility: t(`${base}.traits.flexibility`),
          uvResistance: t(`${base}.traits.uvResistance`),
          easeOfPrinting: t(`${base}.traits.easeOfPrinting`),
          cost: t(`${base}.traits.cost`),
        },
        order: index + 1,
      };
    });
  }, [t]);
}

/**
 * Hook that returns materials as a map by ID
 */
export function useMaterialById(): Record<MaterialId, MaterialSpec> {
  const materials = useMaterials();
  
  return useMemo(() => {
    return materials.reduce((acc, m) => {
      acc[m.id] = m;
      return acc;
    }, {} as Record<MaterialId, MaterialSpec>);
  }, [materials]);
}

// Helper function to get array translations
function getArrayTranslation(t: TranslationFunction, baseKey: string, maxItems: number = 10): string[] {
  const items: string[] = [];
  let index = 0;
  
  // Try to get items with indices 0, 1, 2, 3...
  while (index < maxItems) {
    const key = `${baseKey}.${index}`;
    const value = t(key, { defaultValue: null });
    
    if (value === null || value === key) {
      // Stop when we hit a missing translation
      break;
    }
    
    items.push(value);
    index++;
  }
  
  return items;
}
// --- END: Translated materials data hook ---
